import React from "react"
import {
  ArrayInput,
  BooleanInput,
  Edit,
  FormTab,
  FunctionField,
  SelectInput,
  TabbedForm,
  TextInput,
  SimpleFormIterator,
  FormDataConsumer,
  DateInput,
  NumberInput,
  DateField,
  useRefresh,
  useNotify,
  useFormContext,
} from "react-admin"
import { IconButton, makeStyles } from "@material-ui/core"
import DeleteIcon from "@material-ui/icons/Delete"
import { red } from "@material-ui/core/colors"
import ClaimDeleteBar from "../ClaimDeleteBar"
import TicketPanel from "../Claim/TicketPanel"
import INSURANCE_OPTIONS from "../../lib/constants/insurance-providers"
import { LawFirmOptions } from "../../constants/lawFirms"
import funders from "../../lib/constants/funders.json"
import { db } from "../../firebase"
import firebase from "firebase/compat"
import { useForm } from "react-final-form"

const useStyles = makeStyles(theme => ({
  lastField: {
    marginBottom: theme.spacing(3),
  },
  deleteIcon: {
    color: red[500],
  },
  workflowTrigger: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}))

type Styles = typeof useStyles

const insuranceChoices = [
  ...INSURANCE_OPTIONS.map(opt => ({ id: opt.value, name: opt.label })),
  { id: "other", name: "Other" },
]

const OPTS = {
  YES: "yes",
  NO: "no",
  DONTKNOW: "dont-know",
}

const currentYear = new Date().getFullYear()
const registrationDates = [{ id: "", name: "" } ]

for (let year = currentYear; year >= 1996; year--) {
  registrationDates.push({ id: String(year), name: String(year) })
}

function Aside ({ record }: { record?: Claim }) {
  if (!record) {
    return null
  }

  return (
    <TicketPanel
      userId={record.userId}
      claimId={record.id}
      claim={record}
      type="bmw-claim"
    />
  )
}

function renderUtmParamsField (obj: any) {
  return (
    <ArrayInput source={"utmParams"} label="UTM parameters">
      <SimpleFormIterator disableReordering>
        <FormDataConsumer>
          {({ getSource = () => {} }) => {
            return (
              <div>
                <TextInput
                  label="Source"
                  source={getSource("source") || ""}
                  style={{
                    marginRight: "1rem",
                  }}
                />
                <TextInput
                  label="Value"
                  source={getSource("value") || ""}
                  style={{
                    marginRight: "1rem",
                  }}
                />
              </div>
            )
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  )
}


function valueToBoolean (value: any) {
  if (typeof value === "string") {
    value = value.toLowerCase()
    if (value === "true" || value === "1" || value === "yes") {
      return true
    }
    if (value === "false" || value === "0" || value === "no") {
      return false
    }
  }

  if (value === true || value === 1) {
    return true
  }
  if (value === false || value === 0) {
    return false
  }
  else {
    throw new Error(
      `Value "${value}" can not be coerced to boolean`,
    )
  }
}

function WorkflowDeleteButton ({ className }: { className: string }) {
  const form = useForm()

  function handleClick (event: React.MouseEvent) {
    event.preventDefault()
    if (window.confirm("Are you sure you want to remove this value?")) {
      form.change("workflowTriggeredOn", null) // clear locally
    }
  }

  return (
    <IconButton onClick={handleClick}>
      <DeleteIcon className={className} />
    </IconButton>
  )
}

export default function BmwClaimEdit (
  props: Record<string, unknown>,
) {
  const classes = useStyles()
  const notify = useNotify()
  const refresh = useRefresh()

  return (
    <Edit
      {...props}
      aside={<Aside />}
      transform={data => {
        data.customFieldValues = data.customFieldValues || {}

        data.additionalFields?.forEach(
          (field: { id: string, value: any, datatype: string }) => {
            if (field.value === null || typeof field.value === "undefined") {
              return
            }

            let value: any
            if (field.datatype === "number") {
              value = Number(field.value)
            }
            else if (field.datatype === "boolean") {
              value = valueToBoolean(field.value)
            }
            else {
              value = field.value
            }
            data.customFieldValues[field.id] = { value }
          },
        )

        for (const key in data.customFieldValues) {
          if (data.customFieldValues[key]._value) {
            const dateObject = data.customFieldValues[key]
            let date: string
            if (dateObject.value._seconds) {
              date = new Date(dateObject.value._seconds * 1000)
                .toISOString()
                .split("T")[0]
            }
            else {
              date = dateObject.value
            }
            data.customFieldValues[key] = {
              value: date,
            }
          }
        }

        delete data.additionalFields

        if (data.poaUploaded && !data.poaUploadedAt) {
          data.poaUploadedAt = new Date()
        }

        if (!data.poaUploaded) {
          data.poaUploadedAt = undefined
        }

        if (data.tlRequested && !data.tlRequestedAt) {
          data.tlRequestedAt = new Date()
        }

        if (!data.tlRequested) {
          data.tlRequestedAt = undefined
        }

        if (data.transmittedToLawfirmAt === undefined) {
          data.transmittedToLawfirmAt = null
          data._transmittedToLawfirmAt = null
        }

        if (data.tlRequestedAt === undefined) {
          data.tlRequestedAt = null
          data._tlRequestedAt = null
        }

        if (data.poaUploadedAt === undefined) {
          data.poaUploadedAt = null
          data._poaUploadedAt = null
        }

        return data
      }}
    >
      <TabbedForm
        redirect="show"
        sanitizeEmptyValues={false}
        toolbar={<ClaimDeleteBar path="bmw-claims"/>}
      >
        <FormTab label="Summary">
          <BooleanInput
            source="lead"
            label="Lead"
          />
          <SelectInput
            source="lawFirm"
            allowEmpty={true}
            choices={LawFirmOptions}
            parse={(v: string) => v === "" ? null : v}
            emptyValue={""}
            emptyText="No law firm"
          />
          <SelectInput
            source="type"
            choices={[
              { id: "rejects", name: "Rejects" },
              { id: "bmw-claim", name: "BMW Claim" },
            ]}
          />
          <FormDataConsumer>
            {({ formData, ...rest }) => (
              <SelectInput
                source="funder"
                label="Funder (For CMT)"
                allowEmpty={true}
                choices={
                  formData.ocOlgNotSuitable
                    ? funders.filter(v => v.id !== "Munster")
                    : funders
                }
                parse={(v: string) => v === "" ? null : v}
                emptyValue={""}
                emptyText="Set no funder"
              />
            )}
          </FormDataConsumer>
          <div className={classes.workflowTrigger}>
            <DateInput
              source='workflowTriggeredOn'
              label='Workflow Executed On'
              locales='sv-SE'
              showTime
              disabled
            />
            <FormDataConsumer>
              {() => <WorkflowDeleteButton className={classes.deleteIcon} />}
            </FormDataConsumer>
          </div>
          <TextInput source="leadSource" />
          <TextInput source="vsvId" label="VSV ID"/>
          <SelectInput
            source="claimActionStatus"
            choices={[
              { id: "active", name: "Active" },
              { id: "dead", name: "Dead" },
              { id: "below5k", name: "Below 5k"},
              { id: "onHold", name: "On Hold" },
              { id: "finished", name: "Finished" },
            ]}
          />
          {/* <BooleanInput
            source="hasUploadedPurchaseContract"
            disabled={true}
            label="KV Uploaded"
          />
          <BooleanInput
            source="hasUploadedVehicleRegistrationDocument"
            disabled={true}
            label="FZS Uploaded"
          />
          <BooleanInput
            source="hasUploadedSalesContract"
            disabled={true}
            label="VV Uploaded"
          />
          <BooleanInput
            source="hasUploadedLatestMileage"
            disabled={true}
            label="KM Stand Uploaded"
          />
          */}
          <BooleanInput
            source="poaUploaded"
            label="POA Page Uploaded"
          />
          <TextInput source="notes" />
          <ArrayInput source="utmParams" label="UTM parameters">
            <SimpleFormIterator disableReordering>
              <FormDataConsumer>
                {({ getSource = () => {} }) => (
                  <div>
                    <TextInput
                      label="Source"
                      source={getSource("source") || ""}
                      style={{ marginRight: "1rem" }}
                    />
                    <TextInput
                      label="Value"
                      source={getSource("value") || ""}
                      style={{ marginRight: "1rem" }}
                    />
                  </div>
                )}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </FormTab>
        <FormTab label="Contact Info">
          <TextInput
            label="Claim First Name"
            source="firstName"
          />
          <TextInput
            label="Claim Last Name"
            source="lastName"
          />
          <TextInput
            label="Contract First Name"
            source="purchaseContractFirstName"
          />
          <TextInput
            label="Contract Last Name"
            source="purchaseContractLastName"
          />
          <TextInput
            label="Contract Company"
            source="purchaseContractCompanyName"
          />
          <TextInput source="address" />
          <TextInput source="city" />
          <TextInput source="postcode" />
          <TextInput source="phoneNumber" />
          <SelectInput
            source="whatsappContactedNumber"
            choices={[
              { id: null, name: "None" },
              { id: "0", name: "WA 0 Carl  442" },
              { id: "1", name: "WA 1 Follow Up  975" },
              { id: "2", name: "WA 2 Follow Up 982" },
            ]}
          />
          <TextInput
            label="IBAN"
            source="iban"
            className={classes.lastField}
          />
        </FormTab>
        <FormTab label="BMW Cars">
          <SelectInput
            label="Car Type"
            source="bmwCarData.carType"
            choices={[
              { id: "bmw", name: "BMW" },
              { id: "mini", name: "MINI" },
            ]}
          />
          <SelectInput
            label="Purchase (Year)"
            source="bmwCarData.purchase"
            choices={[
              { id: "<2016", name: "< 2016" },
              { id: ">2016", name: "> 2016" },
            ]}
          />
          <SelectInput
            label="Vehicle Registration (Year)"
            source="bmwCarData.registration"
            choices={[
              { id: "<2010", name: "< 2010" },
              { id: ">2010", name: "> 2010" },
            ]}
          />
          <SelectInput
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <SelectInput
            label="Vehicle Current Mileage"
            source="bmwCarData.mileage"
            choices={[
              { id: "<200k", name: "< 175.000 Km" },
              { id: ">200k", name: "> 175.000 Km" },
            ]}
          />
          <SelectInput
            label="Vehicle Mileage on Sold"
            source="bmwCarData.mileageSold"
            choices={[
              { id: "<200k", name: "< 175.000 Km" },
              { id: ">200k", name: "> 175.000 Km" },
            ]}
          />
          <NumberInput
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberInput
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
        </FormTab>
        <FormTab label='Validation'>
          <TextInput label='Contract First Name'
            source='purchaseContractFirstName' />
          <TextInput label='Contract Last Name'
            source='purchaseContractLastName' />
          <TextInput source='address' />
          <TextInput source='city' />
          <TextInput source='postcode' />
          <TextInput source='phoneNumber' />

          <BooleanInput
            source='differentAddress'
            label='Different Residential Address'
          />

          <TextInput source='livingAddress' label="Residential Address"/>
          <TextInput source='livingCity' label="Residential City"/>
          <TextInput source='livingZipCode' label="Residential Postal Code"/>
          <SelectInput
            source="country"
            label="Residential Country"
            defaultValue="Deutschland"
            choices={[
              { id: "Deutschland", name: "Deutschland" },
              { id: "Österreich", name: "Österreich" },
              { id: "Frankreich", name: "Frankreich" },
              { id: "Italien", name: "Italien" },
              { id: "Spanien", name: "Spanien" },
              { id: "Other", name: "Other" },
            ]}
          />

          <SelectInput
            label="Vehicle Ownership"
            source="bmwCarData.ownership"
            choices={[
              { id: "owner", name: "Owner" },
              { id: "sold", name: "Sold" },
            ]}
          />
          <NumberInput
            label='Current Mileage'
            source='bmwCarData.currentMileage'
          />
          <NumberInput
            label='Mileage on Sales Contract'
            source='bmwCarData.salesMileage'
          />
          <BooleanInput
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
          />
        </FormTab>
        <FormTab label='RSV Info'>
          <BooleanInput
            source='legalExpensesInsurance'
            label='Legal Expenses Insurance'
          />
          <SelectInput
            source='insuranceProvider'
            choices={insuranceChoices}
          />
          <FormDataConsumer>
            {({ formData }) =>
              formData.insuranceProvider === "other" && (
                <TextInput
                  source='insuranceProviderManual'
                  label='Insurance Provider Name'
                  validate={(value: string) =>
                    !value || value.length < 3
                      ? "Minimum 3 characters required"
                      : undefined
                  }
                />
              )
            }
          </FormDataConsumer>
          <TextInput source='legalExpensesInsurancePolicyNumber' />
        </FormTab>

      </TabbedForm>
    </Edit>
  )
}
